import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { parseLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Chat = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000); // Poll every 3 seconds
    return () => clearInterval(interval);
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/${userId}`);
      setMessages(response.data);
      
      // Fetch other user info
      if (!otherUser) {
        // In a real app, you'd have an endpoint to get user by ID
        setOtherUser({ id: userId, nome: 'Usuário' });
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      if (loading) {
        toast.error('Erro ao carregar mensagens');
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API_URL}/api/messages`, {
        receiver_id: userId,
        mensagem: newMessage
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    }
  };

  const formatTime = (dateString) => {
    try {
      // For timestamps (ISO format with time), we can use new Date safely
      // The issue is mainly with date-only strings
      const date = dateString.includes('T') ? new Date(dateString) : parseLocalDate(dateString);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto h-[calc(100vh-200px)] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4 pb-4 border-b border-border">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Chat com {otherUser?.nome || 'Usuário'}
            </h2>
            <p className="text-sm text-muted-foreground">Online</p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 mb-4 bg-card rounded-2xl p-6">
          {messages.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Nenhuma mensagem ainda. Inicie a conversa!
            </div>
          ) : (
            messages.map((message) => {
              const isMe = message.sender_id === user?.id;
              return (
                <div
                  key={message.id}
                  data-testid={`message-${message.id}`}
                  className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                      isMe
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {message.mensagem}
                    </p>
                    <p className={`text-xs mt-1 ${
                      isMe ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    }`}>
                      {formatTime(message.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="flex gap-3">
          <input
            data-testid="message-input"
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Digite sua mensagem..."
            className="flex-1 bg-muted border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-full h-12 px-6"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          />
          <button
            data-testid="send-button"
            type="submit"
            disabled={!newMessage.trim()}
            className="bg-primary text-primary-foreground rounded-full p-3 hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={24} />
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default Chat;